import { Component, HostListener } from '@angular/core';
import { Subscription, lastValueFrom } from 'rxjs';
import { CommonService } from '@app/services/common.service';
import { UsersService } from '@app/services/users.service';
import { AudioService } from '@app/services/audio.service';
import { SitesService } from '@app/services/sites.service';
import { DealerService } from '@app/services/dealer.service';
import { c_components } from 'src/3ui/2components';
import { Helper } from '../../../../../4services/2helper'
import ims from '../../imports'
import { BaseComponent } from '@app/directives/base-component'

@Component({
    selector: 'c_dtable_site_default_settings_for_partition',
    templateUrl: './c_dtable_site_default_settings_for_partition.component.html',
    styleUrls: ['../../common.scss', '../site_default_settings.scss', './c_dtable_site_default_settings_for_partition.component.scss'],
    standalone: false
})
export class c_dtable_site_default_settings_for_partition_component extends BaseComponent {
  isLoading = false;
  isEditMode = false;

  tableHeader = [
    { name: 'SETTINGS', value: 'setting', width: "20%", isCheckbox: false },
    { name: 'CHEKT DEFAULT VALUES', value: 'chekt', width: "20%", isCheckbox: false },
    { name: 'DEALER DEFAULT VALUES', value: 'dealer', width: "60%", isCheckbox: false },
  ]

  // System Audio Alert rows (key ↔ usecase, and CHeKT default tts_id)
  audioAlertRows = [
    { key: 'entryStartsAlarm', label: 'Entry Starts Alarm', usecase: 'ENTRY', defaultTtsId: 2 },
    { key: 'systemAlarmStarts', label: 'System Alarm Starts', usecase: 'SYSTEM', defaultTtsId: 1 },
    { key: 'cameraTriggerWhileInAlarm', label: 'Camera Trigger While In Alarm', usecase: 'CAMERA', defaultTtsId: 4 },
  ];

  // Default TTS (from GET /ttss) + Dealer TTS files (from GET /dealers/{id}/ttss)
  defaultTTSList: any[] = [];
  dealerTTSList: any[] = [];

  // dropdown menu open state (alert key or null) + fixed position
  openMenu: string | null = null;
  menuTop = 0;
  menuLeft = 0;
  menuWidth = 0;

  // audio preview state
  currentPlayingAudio: HTMLAudioElement | null = null;
  isAudioPlay = false;
  playingTtsId: number | null = null;
  loadingTtsId: number | null = null;
  failedTtsId: number | null = null;

  // validate
  isHasError = {
    entryDelay: false,
    exitDelay: false,
    bellDuration: false,
  };

  originSettings = this.buildInitSettings();
  partitionSettings = this.buildInitSettings();

  // full site_default_setting from the server (all required fields), kept to merge on save
  private rawSiteDefaultSetting: any = null;

  constructor(
    public commonService: CommonService,
    private usersService: UsersService,
    private audioService: AudioService,
    private sitesService: SitesService,
    private dealerService: DealerService,
    private helper: Helper,
    private c_components: c_components
  ) { super(); }

  private dealerDefaultMpSettings$w: Subscription;
  watch() {
    this.dealerDefaultMpSettings$w = this.helper.mp_settings.dealerDefaultMpSettings$w().subscribe((v) => this.onChangeSiteDefaultSettings(v));
  }
  unwatch() {
    this.dealerDefaultMpSettings$w?.unsubscribe();
  }

  async ngOnInit() {
    this.isLoading = true;

    this.initData();
    this.watch();
    await this.loadTTS();
    await this.loadData();
    this.isLoading = false;
  }
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stopAudio();
    this.unwatch();
  }

  buildInitSettings() {
    return {
      entryDelay: 60,
      exitDelay: 120,
      bellDuration: 5, // minutes
      audioAlerts: {
        entryStartsAlarm: { ttsId: null, enabled: false },
        systemAlarmStarts: { ttsId: null, enabled: false },
        cameraTriggerWhileInAlarm: { ttsId: null, enabled: false },
      },
    };
  }

  initData() {
    this.partitionSettings = this.buildInitSettings();
    this.originSettings = ims._.cloneDeep(this.partitionSettings);
    this.isHasError = { entryDelay: false, exitDelay: false, bellDuration: false };
  }

  async loadTTS() {
    // Default TTS (system defaults, per usecase)
    try {
      const list = await lastValueFrom(this.audioService.getDefaultTTS());
      this.defaultTTSList = Array.isArray(list) ? list : [];
    } catch (err) {
      console.debug(err);
    }
    // Dealer TTS files (uploaded audio / google tts) - added as select options
    try {
      const dealerId = this.usersService.dealerId();
      if (dealerId) {
        const list = await lastValueFrom(this.dealerService.getTTSList(dealerId));
        this.dealerTTSList = Array.isArray(list) ? list : [];
      }
    } catch (err) {
      console.debug(err);
    }
  }

  async loadData() {
    await this.helper.mp_settings.load_dealer_default_mp_settings();
  }

  onChangeSiteDefaultSettings(value) {
    if (!value) return;
    const { site_default_setting } = value;
    if (!site_default_setting) return;

    let settingsData: any = {};
    try {
      settingsData = JSON.parse(site_default_setting);
    } catch (err) {
      console.debug(err);
      return;
    }

    this.rawSiteDefaultSetting = settingsData;

    const partition = settingsData.partition_default_settings || {};
    const audio = partition.system_audio_alert || {};

    const parsed = {
      entryDelay: settingsData.entry_delay != null ? settingsData.entry_delay : 60,
      exitDelay: settingsData.exit_delay != null ? settingsData.exit_delay : 120,
      bellDuration: partition.bell_duration != null ? Math.round(partition.bell_duration / 60) : 5,
      audioAlerts: {
        entryStartsAlarm: {
          ttsId: audio.entry_starts_alarm?.tts_id ?? null,
          enabled: audio.entry_starts_alarm?.enabled ? true : false,
        },
        systemAlarmStarts: {
          ttsId: audio.system_alarm_starts?.tts_id ?? null,
          enabled: audio.system_alarm_starts?.enabled ? true : false,
        },
        cameraTriggerWhileInAlarm: {
          ttsId: audio.camera_trigger_while_in_alarm?.tts_id ?? null,
          enabled: audio.camera_trigger_while_in_alarm?.enabled ? true : false,
        },
      },
    };

    this.originSettings = ims._.cloneDeep(parsed);
    this.partitionSettings = ims._.cloneDeep(parsed);
  }

  // -----------------------------------------
  // VALIDATE
  validateEntryDelay() {
    this.isHasError.entryDelay = false;
    if (this.partitionSettings.entryDelay < 10 || this.partitionSettings.entryDelay > 900) {
      this.isHasError.entryDelay = true;
    }
  }
  validateExitDelay() {
    this.isHasError.exitDelay = false;
    if (this.partitionSettings.exitDelay < 10 || this.partitionSettings.exitDelay > 900) {
      this.isHasError.exitDelay = true;
    }
  }
  validateBellDuration() {
    this.isHasError.bellDuration = false;
    if (this.partitionSettings.bellDuration > 60) {
      this.partitionSettings.bellDuration = 60;
    } else if (this.partitionSettings.bellDuration < 0) {
      this.partitionSettings.bellDuration = 0;
    }
  }

  isDisabledSave() {
    return Object.values(this.isHasError).some(v => v === true);
  }

  // -----------------------------------------
  // TTS SELECTION
  getAlert(key: string) {
    return this.partitionSettings.audioAlerts[key];
  }
  getOriginAlert(key: string) {
    return this.originSettings.audioAlerts[key];
  }

  // CHeKT default TTS for the row (fixed tts_id: entry=2, system=1, camera=4)
  getRowDefaultTts(row: any): any {
    const id = row?.defaultTtsId;
    if (id == null) return null;
    return this.defaultTTSList.find(t => t.id === id)
      || this.dealerTTSList.find(t => t.id === id)
      || null;
  }
  // resolved CHeKT default (+ playback state) for the CHEKT DEFAULT VALUES column
  chektDefaultTts(row: any): any {
    return this.resolveTts(row?.defaultTtsId);
  }
  chektDefaultName(row: any): string {
    const tts = this.getRowDefaultTts(row);
    if (!tts) return '';
    return tts.title || tts.text || '';
  }

  // resolve a tts_id into a display object (+ playback state), searching dealer then default
  resolveTts(ttsId): any {
    if (ttsId == null) return null;
    let tts = this.dealerTTSList.find(t => t.id === ttsId);
    let isDefault = false;
    if (!tts) {
      tts = this.defaultTTSList.find(t => t.id === ttsId);
      isDefault = true;
    }
    if (!tts) return null;
    return {
      ...tts,
      isDefault,
      isAudioLoad: this.playingTtsId === tts.id,
      isAudioLoading: this.loadingTtsId === tts.id,
      isAudioPlay: this.playingTtsId === tts.id,
      isFailed: this.failedTtsId === tts.id,
    };
  }

  viewTts(key: string) { return this.resolveTts(this.getOriginAlert(key).ttsId); }
  editTts(key: string) { return this.resolveTts(this.getAlert(key).ttsId); }

  // dropdown options: only dealer TTS marked as default (is_default === 1)
  selectableDealerTTS(): any[] {
    return this.dealerTTSList.filter(t => t.is_default === 1);
  }

  toggleMenu(key: string, ev?: MouseEvent) {
    if (this.openMenu === key) {
      this.openMenu = null;
      return;
    }
    this.openMenu = key;
    // position the fixed dropdown right under the selectbox (escapes table overflow clipping)
    const el = ev?.currentTarget as HTMLElement;
    if (el) {
      const r = el.getBoundingClientRect();
      // an ancestor with a transform (e.g. the page's [@page] translateX) becomes the
      // containing block for position:fixed, so offset coords against it.
      const cb = this.getFixedContainingBlock(el);
      const base = cb ? cb.getBoundingClientRect() : { top: 0, left: 0 } as DOMRect;
      this.menuTop = r.bottom - base.top + 2;
      this.menuLeft = r.left - base.left;
      this.menuWidth = r.width;
    }
  }

  private getFixedContainingBlock(el: HTMLElement): HTMLElement | null {
    let node = el.parentElement;
    while (node) {
      const style = getComputedStyle(node);
      if (
        (style.transform && style.transform !== 'none') ||
        (style.perspective && style.perspective !== 'none') ||
        (style.filter && style.filter !== 'none') ||
        (style.willChange && style.willChange.indexOf('transform') !== -1)
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }
  selectTts(key: string, tts: any) {
    this.partitionSettings.audioAlerts[key].ttsId = tts ? tts.id : null;
    this.openMenu = null;
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.openMenu = null;
  }

  // -----------------------------------------
  // AUDIO PREVIEW (mirrors partition-detail)
  toggleAudioPlay(tts: any) {
    if (!tts) return;
    if (this.playingTtsId === tts.id) {
      this.stopAudio();
    } else {
      this.playAudio(tts);
    }
  }

  playAudio(tts: any) {
    if (!tts || tts.id == null) return;

    this.stopAudio();
    this.loadingTtsId = tts.id;
    this.failedTtsId = null;

    // Default TTS: url is directly available
    if (tts.isDefault && tts.url) {
      this.getTTSFile(tts, tts.url);
      return;
    }
    // Dealer TTS: always request a fresh signed url (list url is not playable)
    const dealerId = this.usersService.dealerId();
    if (!dealerId) { this.setAudioFail(tts); return; }
    this.dealerService.getTTS(dealerId, tts.id).subscribe({
      next: (res) => {
        const url = Array.isArray(res) ? res[0]?.url : res?.url;
        if (url) this.getTTSFile(tts, url);
        else this.setAudioFail(tts);
      },
      error: () => this.setAudioFail(tts),
    });
  }

  private getTTSFile(tts: any, url: string) {
    this.sitesService.getTTSFile(url).subscribe({
      next: (blob) => this.parseAudioUrl(tts, blob),
      error: () => this.setAudioFail(tts),
    });
  }

  private parseAudioUrl(tts: any, blob: Blob) {
    const reader = new FileReader();
    reader.onload = () => {
      const audio = new Audio();
      this.currentPlayingAudio = audio;

      audio.addEventListener('loadedmetadata', () => {
        this.loadingTtsId = null;
        this.playingTtsId = tts.id;
        this.isAudioPlay = true;
        audio.play().catch(() => this.setAudioFail(tts));
      });
      audio.addEventListener('ended', () => this.stopAudio());
      audio.addEventListener('error', () => this.setAudioFail(tts));

      audio.src = reader.result as string;
    };
    reader.readAsDataURL(blob);
  }

  stopAudio() {
    if (this.currentPlayingAudio) {
      this.currentPlayingAudio.pause();
      this.currentPlayingAudio.currentTime = 0;
      this.currentPlayingAudio = null;
    }
    this.isAudioPlay = false;
    this.playingTtsId = null;
    this.loadingTtsId = null;
  }

  private setAudioFail(tts: any) {
    this.stopAudio();
    this.failedTtsId = tts?.id ?? null;
  }

  // -----------------------------------------
  // STYLES
  computedTagStyleClass(value) {
    return value ? 'enabled' : 'disabled';
  }

  setTooltipPosition(target) {
    let tooltipBox = document.querySelector('#' + target + '-box');
    let tooltip = document.getElementById(target);
    let rect = tooltipBox.getBoundingClientRect();
    let tooltipRect = tooltip.getBoundingClientRect();
    let posX = rect.left + (rect.right - rect.left) - tooltipRect.width / 2;
    const posY = rect.top - tooltipRect.height - 55;
    if (!this.commonService.isHide) posX = posX - 200;
    tooltip.style.left = posX + 'px';
    tooltip.style.top = posY + 'px';
  }

  // -----------------------------------------
  // ACTIONS
  onClickEdit() {
    this.isEditMode = true;
  }

  onClickCancel() {
    this.stopAudio();
    this.openMenu = null;
    this.partitionSettings = ims._.cloneDeep(this.originSettings);
    this.isHasError = { entryDelay: false, exitDelay: false, bellDuration: false };
    this.isEditMode = false;
  }

  onClickSave() {
    if (this.isDisabledSave()) return;
    this.c_components.dialog.open("warning", {
      header: `Update the partition default settings`,
      contents: `The changes will be applied starting from newly created sites.`,
      submit_btn: "Update",
      submit_class: ["button-primary"],
      icon: 'warning',
      isConfirm: false,
      color: 'orange',
      submit_func: () => {
        this.saveSettings();
      },
      cancel_func: () => this.onClickCancel(),
    });
  }

  async saveSettings() {
    try {
      this.isLoading = true;
      this.stopAudio();
      const alerts = this.partitionSettings.audioAlerts;

      // keep all existing (required) site_default_setting fields, override only what we edit
      const base = this.rawSiteDefaultSetting ? ims._.cloneDeep(this.rawSiteDefaultSetting) : {};
      const site_default_setting = {
        ...base,
        entry_delay: this.partitionSettings.entryDelay,
        exit_delay: this.partitionSettings.exitDelay,
        partition_default_settings: {
          ...(base.partition_default_settings || {}),
          bell_duration: this.partitionSettings.bellDuration * 60,
          system_audio_alert: {
            entry_starts_alarm: {
              tts_id: alerts.entryStartsAlarm.ttsId,
              enabled: alerts.entryStartsAlarm.enabled ? 1 : 0,
            },
            system_alarm_starts: {
              tts_id: alerts.systemAlarmStarts.ttsId,
              enabled: alerts.systemAlarmStarts.enabled ? 1 : 0,
            },
            camera_trigger_while_in_alarm: {
              tts_id: alerts.cameraTriggerWhileInAlarm.ttsId,
              enabled: alerts.cameraTriggerWhileInAlarm.enabled ? 1 : 0,
            },
          },
        },
      };
      await this.helper.mp_settings.update_dealer_default_site_settings({ site_default_setting });
      this.openConfirmDialog('Success', `The partition default settings have been successfully applied for New Sites`, 'done', 'green');
      this.initData();
      await this.loadData();
      this.isEditMode = false;
      this.isLoading = false;
    } catch (err) {
      console.debug(err);
      this.isLoading = false;
    }
  }

  // ------------------------------------------------------------------------
  openConfirmDialog(header = "", msg = "", icon = "done", color = "green") {
    this.c_components.dialog.open("warning", {
      header: header,
      contents: `
        <p>${msg}</b></p>
      `,
      submit_btn: "OK",
      submit_class: ["button-primary"],
      icon: icon,
      isConfirm: true,
      color: color,
      submit_func: () => {},
    });
  }
}
